import type { Match, Player } from '@uefadigital/panenka';
import clsx from 'clsx';
import { useMemo, Fragment } from 'react';
import { useTranslation } from 'react-i18next';

import {
  useGetPlayerAllTimeStatisticsByTypeQuery,
  useGetPlayerDetailsQuery,
  useGetTeamsForPlayersQuery,
} from 'api/fspApi';
import { TournamentStats } from 'components/TournamentStats';
import type { Ranking } from 'types/ApiResponseInterfaces';
import { isFemale } from 'utils/helpers';

export interface ListItem {
  id: number;
  displayOfficialName: string;
  country: string;
  amount: number;
  avatarImage: string;
  playerName: string;
  clubCrest: string;
}

export interface TournamentStatsProps {
  matchData: Match;
  isDesktop: boolean;
  competitionId: string;
  seasonYear: string;
  isPDFPrint?: boolean;
}

const getStatistic = (players: Array<Ranking>, statisticName: string, playerDetails: Array<Player>) => {
  const playerWithFilteredStatistic = [...players].reduce((acc: Array<Ranking>, player: Ranking) => {
    const filteredStatistic = player.statistics.filter((el) => el.name === statisticName);

    if (filteredStatistic[0]?.value !== '0') {
      player = {
        playerId: player.playerId,
        player: {
          ...player.player,
          imageUrl: playerDetails.find((details) => details.id === player.playerId)?.imageUrl ?? '',
        },
        statistics: [...filteredStatistic],
      };

      return [...acc, player];
    }

    return acc;
  }, []);

  const sortedPlayers = playerWithFilteredStatistic.sort(
    (player, nextPlayer) => Number(nextPlayer.statistics[0]?.value) - Number(player.statistics[0]?.value),
  );

  return sortedPlayers.slice(0, 6);
};

export const BestPlayersStats = ({
  matchData,
  isDesktop,
  competitionId,
  seasonYear,
  isPDFPrint = false,
}: TournamentStatsProps) => {
  const GOALS = 'goals';
  const ASSISTS = 'assists';
  const DISTANCE_COVERED = 'distance_covered';
  const PASS_ACCURACY = 'passes_accuracy';
  const { t } = useTranslation();

  const {
    data: playersStatistic = [],
    error: playersStatisticError,
    isLoading: playersStatisticLoading,
  } = useGetPlayerAllTimeStatisticsByTypeQuery(matchData.id);

  const playerIds: string[] = useMemo(() => playersStatistic.map((player) => player.playerId), [playersStatistic]);

  const { data: teamForPlayers = [], isLoading: teamForPlayersLoading } = useGetTeamsForPlayersQuery({
    id: matchData.competition.id,
    playerIds,
    seasonYear: matchData.seasonYear,
  });

  const { data: playerDetails = [], isLoading: playerDetailsLoading } = useGetPlayerDetailsQuery({
    id: competitionId,
    seasonYear,
    playerIds,
  });

  const cards = [
    {
      name: 'goals',
      teamStats: [],
      playerStats: getStatistic(playersStatistic, GOALS, playerDetails),
      statistics: GOALS,
      teamForPlayers,
      isError: playersStatistic.length === 0 || !!playersStatisticLoading,
    },
    {
      name: 'assists',
      teamStats: [],
      playerStats: getStatistic(playersStatistic, ASSISTS, playerDetails),
      statistics: ASSISTS,
      teamForPlayers,
      isError: playersStatistic.length === 0 || !!playersStatisticLoading,
    },
    {
      name: 'distanced_covered_km',
      teamStats: [],
      playerStats: getStatistic(playersStatistic, DISTANCE_COVERED, playerDetails),
      statistics: DISTANCE_COVERED,
      teamForPlayers,
      isError: playersStatistic.length === 0 || !!playersStatisticLoading,
    },
    {
      name: 'pass_accuracy_per',
      teamStats: [],
      playerStats: getStatistic(playersStatistic, PASS_ACCURACY, playerDetails),
      statistics: PASS_ACCURACY,
      teamForPlayers,
      isError: playersStatistic.length === 0 || !!playersStatisticLoading,
    },
  ];

  if (
    teamForPlayersLoading ||
    playersStatisticError ||
    playersStatisticLoading ||
    !playersStatistic.length ||
    playerDetailsLoading
  ) {
    return <div />;
  }

  return (
    <Fragment>
      <div
        className={clsx(
          'pk-font-size--xl pk-font-size-md--xl3 pk-mb--xs pk-mt--m pk-mt-md--xl2 pk-text--text-01 pr-font-size--18 pk-font--comp-bold pk-font-style--normal',
          isDesktop ? 'pk-line-height--sm' : 'pk-line-height--md',
        )}
      >
        {t(isFemale(competitionId) ? 'top_player_stats_f' : 'best_player_stats')}
      </div>

      {isDesktop ? (
        <span className="pk-text--text-01 pk-font-size--l pk-line-height--md pr-font-size--18 pk-font--base-regular">
          {t('current_match_only')}
        </span>
      ) : (
        ''
      )}
      {isDesktop ? <div className="pk-mb--xs" /> : ''}
      <TournamentStats cards={cards} smallMarginHorizontal isPDFPrint={isPDFPrint} />
    </Fragment>
  );
};
